"use client";

interface StatComparisonBarProps {
  label: string;
  myValue: number;
  opponentValue: number;
  myLabel?: string;
  opponentLabel?: string;
  invertColors?: boolean;
  isPercentage?: boolean;
}

export function StatComparisonBar({
  label,
  myValue,
  opponentValue,
  myLabel,
  opponentLabel,
  invertColors = false,
  isPercentage = false,
}: StatComparisonBarProps) {
  const maxValue = Math.max(myValue, opponentValue, 1);
  const myPercent = isPercentage ? myValue : (myValue / maxValue) * 100;
  const opponentPercent = isPercentage
    ? opponentValue
    : (opponentValue / maxValue) * 100;

  // Determine who's winning for color coding
  const myWinning = invertColors
    ? myValue < opponentValue
    : myValue > opponentValue;
  const opponentWinning = invertColors
    ? opponentValue < myValue
    : opponentValue > myValue;

  return (
    <div className="space-y-1.5">
      {/* Label row */}
      <div className="flex items-center justify-center">
        <span className="text-xs text-muted text-center">{label}</span>
      </div>

      {/* Values and bars */}
      <div className="flex items-center gap-2">
        {/* My value */}
        <span
          className={`w-8 text-right text-sm font-bold ${
            myWinning
              ? "text-success-400"
              : myValue === opponentValue
              ? "text-default-500"
              : "text-danger-400"
          }`}
        >
          {isPercentage ? `${myValue}%` : myValue}
        </span>

        {/* Visual bar container */}
        <div className="flex-1 flex items-center gap-1 h-5">
          {/* My bar (grows from center to left) */}
          <div className="flex-1 flex justify-end">
            <div
              className={`h-2 rounded-l-full transition-[width] duration-500 ${
                myWinning
                  ? "bg-gradient-to-l from-success-500 to-success-600"
                  : myValue === opponentValue
                  ? "bg-gradient-to-l from-secondary-500 to-secondary-600"
                  : "bg-gradient-to-l from-danger-500 to-danger-600"
              }`}
              style={{ width: `${myPercent}%` }}
            />
          </div>

          {/* Center divider */}
          <div className="w-px h-4 bg-primary/40" />

          {/* Opponent bar (grows from center to right) */}
          <div className="flex-1 flex justify-start">
            <div
              className={`h-2 rounded-r-full transition-[width] duration-500 ${
                opponentWinning
                  ? "bg-gradient-to-r from-success-500 to-success-600"
                  : myValue === opponentValue
                  ? "bg-gradient-to-r from-secondary-500 to-secondary-600"
                  : "bg-gradient-to-r from-danger-500 to-danger-600"
              }`}
              style={{ width: `${opponentPercent}%` }}
            />
          </div>
        </div>

        {/* Opponent value */}
        <span
          className={`w-8 text-left text-sm font-bold ${
            opponentWinning
              ? "text-success-400"
              : myValue === opponentValue
              ? "text-default-500"
              : "text-danger-400"
          }`}
        >
          {isPercentage ? `${opponentValue}%` : opponentValue}
        </span>
      </div>
    </div>
  );
}
